<template>
  <div style="width: 100%">
    <v-card
      v-if="relatorio"
      flat
      class="pa-2"
    >
      <!-- Formação Acadêmica -->
      <v-row>
        <v-col cols="12">
          <app-title texto="FORMAÇÃO ACADÊMICA" />
        </v-col>
      </v-row>

      <!-- Escolaridade -->
      <v-row>
        <v-col cols="12">
          <app-title texto="ESCOLARIDADE" cor="secondary" />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <pie-chart
            titulo="Comparativo de Níveis de Escolaridade"
            :fonte="`Fonte: Sistema Desenvolva, ${ano}`"
            :dados="dadosPieChartEscolaridade.series"
            :dados-complementares="dadosPieChartEscolaridade.subSeries"
            class="chart"
            :is-get-svg="isGetSvg.escolaridade"
            @svg="(value) => svg.escolaridade = value"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <app-title texto="RESULTADOS DE ESCOLARIDADE" cor="secondary" />
        </v-col>
        <v-col cols="12">
          <tabela-resultado-escolaridade
            :relatorio="relatorio"
          />
        </v-col>
      </v-row>

      <!-- Formação Complementar -->
      <v-row>
        <v-col cols="12">
          <app-title texto="FORMAÇÃO COMPLEMENTAR" />
        </v-col>
      </v-row>

      <!-- Idioma -->
      <v-row>
        <v-col cols="12">
          <app-title texto="IDIOMA" cor="secondary" />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <bar-chart
            :categorias="dadosBarChartIdioma.categorias"
            :dados="dadosBarChartIdioma.dados"
            :fonte="`Fonte: Sistema Desenvolva, ${ano}`"
            :tooltip="tooltipBarChart"
            class="chart"
            descricao-eixo-y="Quantidade"
            titulo="Comparativo de Idiomas"
            exibe-categorias
            :is-get-svg="isGetSvg.idioma"
            @svg="(value) => svg.idioma = value"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <app-title texto="RESULTADOS DE IDIOMA" cor="secondary" />
        </v-col>
        <v-col cols="12">
          <tabela-resultado-idioma
            :relatorio="relatorio"
          />
        </v-col>
      </v-row>

      <!-- Capacitação -->
      <v-row>
        <v-col cols="12">
          <app-title texto="INSTRUTOR DE CAPACITAÇÃO" cor="secondary" />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <pie-chart
            :fonte="`Fonte: Sistema Desenvolva, ${ano}`"
            :dados="dadosPieChartCapacitacao.series"
            :dados-complementares="dadosPieChartCapacitacao.subSeries"
            titulo="Comparativo de Interesses<br>Instrutor de Capacitação"
            class="chart"
            :is-get-svg="isGetSvg.capacitacao"
            @svg="(value) => svg.capacitacao = value"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <app-title texto="RESULTADOS DE INTERESSES: INSTRUTOR DE CAPACITAÇÃO" cor="secondary" />
        </v-col>
        <v-col cols="12">
          <tabela-resultado-capacitacao
            :relatorio="relatorio"
          />
        </v-col>
      </v-row>

      <!-- Relatório PDF -->
      <v-row
        class="justify-end mb-1"
      >
        <v-tooltip
          max-width="400"
          top
        >
          <template v-slot:activator="{ on }">
            <v-btn
              color="success"
              @click="getRelatorio()"
              v-on="on"
            >
              <v-icon>get_app</v-icon>
              BAIXAR PDF
            </v-btn>
          </template>
          <span>Clique para baixar o relatório dos dados em formato PDF.</span>
        </v-tooltip>
      </v-row>
    </v-card>
  </div>
</template>

<script>
import AppTitle from '@/components/core/AppTitle';
import BarChart from '@/components/chart/BarChart';
import PieChart from '@/components/chart/PieChart';
import { generatePDF, preparePdf, convertSvg } from '@/modules/relatorioPDF';
import TabelaResultadoIdioma from './TabelaResultadoGeralFormacaoIdioma';
import TabelaResultadoEscolaridade from './TabelaResultadoGeralFormacaoEscolaridade';
import TabelaResultadoCapacitacao from './TabelaResultadoGeralFormacaoCapacitacao';

export default {
  name: 'RelatorioPerfilGeralFormacao',

  components: {
    AppTitle,
    BarChart,
    PieChart,
    TabelaResultadoIdioma,
    TabelaResultadoEscolaridade,
    TabelaResultadoCapacitacao,
  },

  props: {
    relatorio: {
      type: Object,
      required: true,
    },
    ano: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      tooltipBarChart: {
        headerFormat: '<span style="padding:2px;"><b>{point.key}</b></span><table>',
        pointFormat:
          '<tr>'
          + '<td style="color:{series.color};padding:0">Quantidade: </td>'
          + '<td style="padding:2px;text-align:right"><span>{point.y}</span></td>'
          + '</tr>',
        footerFormat: '</table>',
      },
      isGetSvg: {
        escolaridade: false,
        idioma: false,
        capacitacao: false,
      },
      svg: {
        escolaridade: null,
        idioma: null,
        capacitacao: null,
      },
    };
  },

  computed: {
    dadosPieChartEscolaridade() {
      return {
        series: [
          {
            name: 'Níveis de Escolaridade',
            data: this.relatorio.niveisEscolaridade.filter((n) => n.quantidade > 0)
              .map((n) => ({
                name: n.nome,
                y: n.quantidade,
                drilldown: n.id,
              })).sort((a, b) => {
                if (a.y < b.y) { return 1; }
                if (a.y > b.y) { return -1; }
                return 0;
              }),
          },
        ],
        subSeries: this.relatorio.niveisEscolaridade.filter((n) => n.quantidade > 0)
          .map((n) => ({
            name: n.nome,
            id: n.id,
            data: this.relatorio.unidades.map((u) => {
              const nivelEscolaridade = u.niveisEscolaridade.find((nE) => nE.id === n.id);
              return {
                name: u.sigla,
                y: nivelEscolaridade ? nivelEscolaridade.quantidade : 0,
              };
            }).filter((u) => u.y > 0)
              .sort((a, b) => {
                if (a.y < b.y) { return 1; }
                if (a.y > b.y) { return -1; }
                return 0;
              }),
          })),
      };
    },
    dadosBarChartIdioma() {
      const dados = this.relatorio.idiomas.map((i) => ({
        ...i,
        name: i.nome,
        y: i.quantidade,
      })).sort((a, b) => {
        if (a.y < b.y) { return 1; }
        if (a.y > b.y) { return -1; }
        return 0;
      });

      return {
        dados: [{
          name: 'Idioma',
          data: dados,
          color: '#2D5269',
        }],
        categorias: dados.map((a) => a.nome),
      };
    },
    dadosPieChartCapacitacao() {
      return {
        series: [
          {
            name: 'Respostas',
            data: [
              {
                name: 'Sim',
                y: this.relatorio.quantidadeInstrutorCapacitacao,
                drilldown: this.relatorio.quantidadeInstrutorCapacitacao > 0 ? 'sim' : null,
              },
              {
                name: 'Não',
                y: this.relatorio.quantidadeNaoInstrutorCapacitacao,
              },
              {
                name: 'Não respondeu',
                y: this.relatorio.quantidadeNaoRespondeuInstrutorCapacitacao,
                color: 'grey',
              },
            ],
          },
        ],
        subSeries: [
          {
            name: 'Sim',
            id: 'sim',
            data: this.relatorio.unidades.map((u) => ({
              name: u.sigla,
              y: u.quantidadeInstrutorCapacitacao,
            })).filter((u) => u.y > 0)
              .sort((a, b) => {
                if (a.y < b.y) { return 1; }
                if (a.y > b.y) { return -1; }
                return 0;
              }),
          },
        ],
      };
    },
  },

  methods: {
    async getRelatorio() {
      this.isGetSvg.escolaridade = true;
      this.isGetSvg.idioma = true;
      this.isGetSvg.capacitacao = true;
      await preparePdf(async () => {
        const documento = {
          nome: 'relatorio-geral-formacao-perfil',
          orientacao: 'landscape',
          cabecalho: {
            titulo: 'Relatório Geral da Formação do Perfil',
            itens: [
              `Unidade(s): ${this.relatorio.unidades.map((u) => u.sigla).join(', ')}`,
            ],
          },
          blocos: [
            {
              titulo: 'GRÁFICOS',
              subtitulo: 'ESCOLARIDADE',
              itens: [
                { tipo: 'imagem', valor: await convertSvg(this.svg.escolaridade) },
              ],
            },
            {
              titulo: 'RESULTADOS',
              subtitulo: 'ESCOLARIDADE',
              itens: [{
                tipo: 'tabela',
                valor: {
                  colunas: [
                    'UNIDADE',
                    ...this.relatorio.niveisEscolaridade.map((d) => d.nomeAbreviado.split(' ')
                      .join('\n')
                      .toUpperCase()),
                    'TOTAL DE\nSERVIDO-\nRES',
                  ],
                  linhas: this.relatorio.unidades.map((u) => ([
                    `${u.nome} (${u.sigla})`,
                    ...u.niveisEscolaridade.map((d) => d.quantidade),
                    u.quantidadePerfis,
                  ])),
                  rodape: [
                    '',
                    '',
                    ...this.relatorio.niveisEscolaridade.map((d) => d.quantidade),
                    this.relatorio.quantidadePerfis,
                  ],
                },
              }],
            },
            {
              titulo: 'GRÁFICOS',
              subtitulo: 'IDIOMA',
              itens: [
                { tipo: 'imagem', valor: await convertSvg(this.svg.idioma), largura: 160 },
              ],
            },
            {
              titulo: 'RESULTADOS',
              subtitulo: 'IDIOMA',
              itens: [{
                tipo: 'tabela',
                valor: {
                  colunas: [
                    'UNIDADE',
                    ...this.relatorio.idiomas.map((d) => d.nome.split(' ')
                      .join('\n')
                      .toUpperCase()),
                  ],
                  linhas: this.relatorio.unidades.map((u) => ([
                    `${u.nome} (${u.sigla})`,
                    ...u.idiomas.map((d) => d.quantidade),
                  ])),
                  rodape: [
                    '',
                    '',
                    ...this.relatorio.idiomas.map((d) => d.quantidade),
                  ],
                },
              }],
            },
            {
              titulo: 'GRÁFICOS',
              subtitulo: 'INSTRUTOR DE CAPACITAÇÃO',
              itens: [
                { tipo: 'imagem', valor: await convertSvg(this.svg.capacitacao) },
              ],
            },
            {
              titulo: 'RESULTADOS',
              subtitulo: 'INSTRUTOR DE CAPACITAÇÃO',
              itens: [{
                tipo: 'tabela',
                valor: {
                  colunas: [
                    'UNIDADE',
                    'INTERESSADOS',
                    'NÃO\nINTERESSADOS',
                    'NÃO\nRESPONDEU',
                    'TOTAL DE\nSERVIDORES',
                    'CURSOS\nCADASTRADOS',
                  ],
                  linhas: this.relatorio.unidades.map((u) => ([
                    `${u.nome} (${u.sigla})`,
                    u.quantidadeInstrutorCapacitacao,
                    u.quantidadeNaoInstrutorCapacitacao,
                    u.quantidadeNaoRespondeuInstrutorCapacitacao,
                    u.quantidadePerfis,
                    u.cursosCapacitacao.length,
                  ])),
                  rodape: [
                    '',
                    '',
                    this.relatorio.quantidadeInstrutorCapacitacao,
                    this.relatorio.quantidadeNaoInstrutorCapacitacao,
                    this.relatorio.quantidadeNaoRespondeuInstrutorCapacitacao,
                    this.relatorio.quantidadePerfis,
                    this.relatorio.quantidadeCursosCapacitacao,
                  ],
                },
              }],
            },
          ],
        };
        generatePDF(documento);
      });
      this.isGetSvg.escolaridade = false;
      this.isGetSvg.idioma = false;
      this.isGetSvg.capacitacao = false;
    },
  },
};
</script>

<style scoped>
.chart {
  width: 100%;
  max-height: 400px;
  font-family: Roboto, sans-serif;
}

.chart >>> .highcharts-legend-item rect {
  display: none;
}

.chart >>> .highcharts-legend-item {
  pointer-events: none;
}
</style>
